/**
 * O Postgres resolve o filtro salvo inteiro em SQL?
 *
 * O push-down do PostgREST (`applyDealSavedFilterToQuery`) só sabe traduzir
 * colunas de `deals` e `deals.custom_data.*`. Qualquer regra relacional
 * (`companies.*`, `leads.*`) ou grupo `OR` cai como "residual" e joga a lista no
 * fallback que **baixa todas as linhas** em lotes de 100 pra filtrar em memória —
 * 12.641 negócios com embed pesado = ~127 requisições sequenciais, que é a origem
 * do erro intermitente em `/deals/list`.
 *
 * Só que o `build_saved_filter_where` (usado por `get_deals_page`/`get_deals_count`)
 * cobre bem mais que isso: relações belongsTo, custom fields de entidade
 * relacionada e grupos OR. Quando ele cobre 100% das regras, a lista pode usar o
 * RPC paginado e não baixar nada além da página.
 *
 * ⚠️ O construtor SQL **descarta em silêncio** (predicado `true`) a regra que não
 * sabe traduzir — tags, operador desconhecido, coluna inexistente. Por isso quem
 * decide é o próprio banco, via `saved_filter_residual_rule_count`: só confiamos
 * no caminho server-side quando ele devolve 0. Qualquer erro/dúvida → mantém o
 * motor client-side (fail-safe: no pior caso fica lento, nunca devolve conjunto
 * errado).
 */
import { supabase } from "@/integrations/supabase/client";

const COVERAGE_CACHE_TTL = 30_000; // igual ao cache de fetchFilterData
const coverageCache = new Map<string, { residual: number | null; timestamp: number }>();

/** Limpa o cache de cobertura (ex.: após criar/editar/excluir filtro). */
export function invalidateSavedFilterCoverageCache(filterId?: string | null): void {
  if (filterId) {
    const prefix = `${filterId}:`;
    for (const key of [...coverageCache.keys()]) {
      if (key.startsWith(prefix)) coverageCache.delete(key);
    }
    return;
  }
  coverageCache.clear();
}

/**
 * Nº de regras que o construtor SQL NÃO cobre. `null` quando não foi possível
 * saber (erro de rede/RPC ausente) — o caller trata como "não cobre".
 */
async function fetchResidualRuleCount(
  filterId: string,
  primaryEntity: string,
): Promise<number | null> {
  try {
    const { data, error } = await supabase.rpc("saved_filter_residual_rule_count", {
      p_filter_id: filterId,
      p_primary_entity: primaryEntity,
    });
    if (error) return null;
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * `true` só quando o banco resolve TODAS as regras do filtro em SQL — aí a lista
 * pode usar `get_deals_page` em vez de baixar a org inteira pra filtrar em memória.
 */
export async function isSavedFilterFullyServerResolvable(
  filterId: string | null | undefined,
  primaryEntity: string,
): Promise<boolean> {
  if (!filterId) return false;

  const cacheKey = `${filterId}:${primaryEntity}`;
  const cached = coverageCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < COVERAGE_CACHE_TTL) {
    return cached.residual === 0;
  }

  const residual = await fetchResidualRuleCount(filterId, primaryEntity);
  coverageCache.set(cacheKey, { residual, timestamp: Date.now() });
  return residual === 0;
}
